import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';

import TurnstileChallenge from '../../../shared/TurnstileChallenge';

const axios = require("axios").default;

const formatCents = (cents, currency) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: currency || 'USD',
}).format(cents / 100);

const UNAVAILABLE_MESSAGE = "Gift cards can't be applied right now. Please try again.";
const DONATION_MESSAGE = "Gift cards can't be used on orders with a donation. Remove the donation to use a gift card.";

const GiftCardRedemptionField = ({
  csrfToken,
  confirm,
  ticketReservation,
  ticketReservationChanged,
  hasDonation,
  onGiftCardsChanged
}) => {
  const turnstileRef = useRef(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const redemptionsPath = `/confirms/${confirm.id}/ticket_reservations/${ticketReservation.token}/gift_card_redemptions`;
  const appliedCards = ticketReservation.applied_gift_card_summaries || [];
  const fullyCovered = appliedCards.length > 0 && !ticketReservation.stripe_required;
  const requiredCardPaymentCents = ticketReservation.gift_card_required_card_payment_cents;
  const codeEntryHidden = fullyCovered || Boolean(ticketReservation.gift_card_entry_closed);

  const reservationUpdated = (data) => {
    ticketReservationChanged({
      ...ticketReservation,
      ...data,
      in_memory_fees: data.in_memory_fees ?? ticketReservation.in_memory_fees
    });
    onGiftCardsChanged();
  };

  const turnstileToken = () => {
    if (!captchaRequired || !turnstileRef.current) {
      return Promise.resolve("");
    }

    return turnstileRef.current.executeAsync();
  };

  const applyCode = () => {
    setError(null);
    setIsSubmitting(true);
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    return turnstileToken()
      .then((turnstileTokenValue) => axios.post(redemptionsPath, {
        gift_card_code: code,
        turnstile_token: turnstileTokenValue
      }))
      .then(({ data }) => {
        setCode("");
        reservationUpdated(data);
      })
      .catch((requestError) => {
        const body = requestError && requestError.response && requestError.response.data;
        const message = body && body.errors && body.errors[0];

        setError(message || UNAVAILABLE_MESSAGE);
        setCaptchaRequired(Boolean(body && body.captcha_required) || captchaRequired);

        if (turnstileRef.current) {
          turnstileRef.current.reset();
        }
      })
      .finally(() => setIsSubmitting(false));
  };

  const removeCard = (redemptionId) => {
    setError(null);
    setNotice(null);
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    return axios.delete(`${redemptionsPath}/${redemptionId}`)
      .then(({ data }) => {
        const releasedCodes = data.released_gift_card_code_last4s || [];

        if (releasedCodes.length > 0) {
          setNotice(`No longer needed for this order, so removed: ${releasedCodes.map((last4) => `gift card •••• ${last4}`).join(", ")}.`);
        }
        reservationUpdated(data);
      })
      .catch(() => setError("We couldn't remove that gift card. Please try again."));
  };

  return (
    <div className="row gift-card-redemption-field"
         style={{"marginTop": "23px"}}>
      <div className="col-12">
        <p className="mb-1"
           style={{"fontSize": "16px"}}>
          <strong>Gift card</strong>
        </p>
        {hasDonation ? (
          <p className="mb-0 text-muted">{DONATION_MESSAGE}</p>
        ) : (
          <>
            {appliedCards.map((card) => (
              <div key={card.id}
                   className="d-flex align-items-center justify-content-between mb-2 applied-gift-card">
                <div>
                  <strong>Gift card •••• {card.code_last4}</strong>
                  <div className="text-muted">
                    {formatCents(card.amount_cents, confirm.gift_card_currency)} applied · {formatCents(card.balance_after_cents, confirm.gift_card_currency)} left on card
                  </div>
                </div>
                <button type="button"
                        className="btn btn-link p-0"
                        onClick={() => removeCard(card.id)}>
                  Remove
                </button>
              </div>
            ))}
            {requiredCardPaymentCents ? (
              <p className="text-muted mb-2 gift-card-required-card-payment">
                Part of this order must be paid by card ({formatCents(requiredCardPaymentCents, confirm.gift_card_currency)}), so the rest of your balance stays on your gift card.
              </p>
            ) : null}
            {fullyCovered ? (
              <p className="mb-0">Your gift card covers this order. No payment card needed.</p>
            ) : null}
            {codeEntryHidden ? null : (
              <>
                <p>Have a gift card? Enter the code to use it on this order.</p>
                <div className="form-row">
                  <div className="col-8">
                    <input type="text"
                           placeholder="XXXX-XXXX-XXXX-XXXX"
                           autoComplete="off"
                           className={`form-control ${error ? "border-danger" : ""}`}
                           value={code}
                           onChange={(event) => setCode(event.target.value)} />
                  </div>
                  <div className="col-4">
                    <button type="button"
                            className="btn btn-danger px-2 py-1"
                            disabled={code.trim().length === 0 || isSubmitting}
                            onClick={applyCode}>
                      Apply
                    </button>
                  </div>
                </div>
                {captchaRequired ? (
                  <TurnstileChallenge ref={turnstileRef}
                                      sitekey={gon.turnstile_site_key} />
                ) : null}
              </>
            )}
            {notice ? (
              <p className="text-muted mt-2 mb-0 gift-card-notice">{notice}</p>
            ) : null}
            {error ? (
              <p className="text-danger mt-2 mb-0">{error}</p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

GiftCardRedemptionField.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  confirm: PropTypes.object.isRequired,
  ticketReservation: PropTypes.object.isRequired,
  ticketReservationChanged: PropTypes.func.isRequired,
  hasDonation: PropTypes.bool,
  onGiftCardsChanged: PropTypes.func.isRequired
};

export default GiftCardRedemptionField;
